import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import VaccinesIcon from '@mui/icons-material/Vaccines'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import API from '../../../../services/API'
import EmptyState from '../../../../components/EmptyState/EmptyState'
import { DiscardDialog, HandoverDialog } from './PreparedActionDialogs'
import { bagName, doseSummary, expiryLabel, expiryTone } from './preparedHelpers'

// Re-render every 30s so expiry countdowns stay current
export const useMinuteTick = () => {
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000)
    return () => clearInterval(id)
  }, [])
}

export const PreparedCard = ({ prepared, onHandover, onDiscard, showHolder }) => {
  const tone = expiryTone(prepared.expiresAt)
  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderLeft: 5,
        borderLeftColor:
          tone === 'default' ? 'divider' : `${tone}.main`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Box minWidth={0}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={800}>{prepared.medicineName}</Typography>
            {prepared.isControlled && (
              <Chip size="small" label="CD" color="warning" sx={{ borderRadius: 1 }} />
            )}
            {prepared.label && (
              <Chip size="small" variant="outlined" label={prepared.label} sx={{ borderRadius: 1 }} />
            )}
          </Stack>
          <Typography variant="body2">{doseSummary(prepared)}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Drawn up {moment(prepared.preparedAt).format('DD/MM HH:mm')} by{' '}
            {prepared.preparedBy?.name ?? 'unknown'} from {bagName(prepared.fromLocation)}
            {prepared.batchNumbers?.length
              ? ` · Batch ${prepared.batchNumbers.join(', ')}`
              : ''}
          </Typography>
          {showHolder && (
            <Typography variant="caption" color="text.secondary" component="div">
              Held by <strong>{prepared.heldBy?.name ?? 'unknown'}</strong>
            </Typography>
          )}
          {prepared.administrations?.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="div">
              {prepared.administrations.length} dose
              {prepared.administrations.length === 1 ? '' : 's'} given so far
            </Typography>
          )}
        </Box>
        <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} gap={1} flexShrink={0}>
          <Chip
            size="small"
            color={tone}
            variant={tone === 'error' ? 'filled' : 'outlined'}
            label={expiryLabel(prepared.expiresAt)}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
          {(onHandover || onDiscard) && (
            <Stack direction="row" gap={1}>
              {onHandover && !prepared.isExpired && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => onHandover(prepared)}
                >
                  Hand over
                </Button>
              )}
              {onDiscard && (
                <Button
                  size="small"
                  variant={prepared.isExpired ? 'contained' : 'outlined'}
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => onDiscard(prepared)}
                >
                  Discard
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}

// Syringes the signed-in clinician is carrying. Used on its own page and on
// the drug bag page (hideWhenEmpty). Bump `refreshKey` to reload.
export const MyPreparedMedicinesPanel = ({ refreshKey, hideWhenEmpty, title }) => {
  useMinuteTick()
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [handover, setHandover] = useState(null)
  const [discard, setDiscard] = useState(null)

  const load = useCallback(() => {
    API.get('/medsmx/prepared/mine')
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setList(res.data)
          setFetchError(false)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  if (hideWhenEmpty && !loading && !fetchError && list.length === 0) return null

  const expired = list.filter((p) => moment(p.expiresAt).isBefore(moment())).length

  return (
    <Stack gap={1.5}>
      {title && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontWeight={900}>{title}</Typography>
          {expired > 0 && (
            <Chip size="small" color="error" label={`${expired} expired`} sx={{ borderRadius: 1 }} />
          )}
        </Stack>
      )}
      {loading && <LinearProgress />}
      {fetchError && <Alert severity="error">Unable to load your drawn-up medicines.</Alert>}
      {!loading && !fetchError && list.length === 0 && (
        <EmptyState
          icon={VaccinesIcon}
          title="You aren't carrying any drawn-up medicines"
          description="Draw up a medicine from a drug bag (Check Meds Bag → Draw up) and it will be listed here until it is given, handed over or discarded."
          sx={{ boxSizing: 'border-box' }}
        />
      )}
      {list.map((prepared) => (
        <PreparedCard
          key={prepared._id}
          prepared={{
            ...prepared,
            isExpired: moment(prepared.expiresAt).isBefore(moment()),
          }}
          onHandover={setHandover}
          onDiscard={setDiscard}
        />
      ))}
      <HandoverDialog prepared={handover} onClose={() => setHandover(null)} onDone={load} />
      <DiscardDialog prepared={discard} onClose={() => setDiscard(null)} onDone={load} />
    </Stack>
  )
}

const MyPreparedMedicines = () => (
  <Stack gap={2} sx={{ maxWidth: 1000, mx: 'auto', width: '100%' }}>
    <Box>
      <Typography variant="title2" component="div">
        My Drawn-up Medicines
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        Syringes you are carrying. Give them from the ePCR, hand them to a
        colleague, or discard them before they expire.
      </Typography>
    </Box>
    <MyPreparedMedicinesPanel />
  </Stack>
)

export default MyPreparedMedicines
